import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Statistic, Progress, Typography, Spin, Space, Alert, Input, Tag, Empty, theme } from 'antd';
import { palette } from '../../theme/palette';
import {
  CreditCardOutlined,
  FileTextOutlined,
  CalendarOutlined,
  WarningOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { userService, companiesService, certificatesService } from '../../services/api';

const { Title, Text } = Typography;

// Presentación del estado del CSD en un solo lugar (color + etiqueta).
const CSD_META = {
  valid: { type: 'success', label: 'Vigente' },
  expiring_soon: { type: 'warning', label: 'Por vencer' },
  expired: { type: 'danger', label: 'Vencido' },
  missing: { type: 'warning', label: 'Pendiente' },
  unknown: { type: 'secondary', label: 'Sin validar' },
};

const parseMysqlDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes('T')) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = raw.split(/[- :]/).filter(Boolean);
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const hh = parts.length >= 4 ? Number(parts[3]) : 0;
  const mm = parts.length >= 5 ? Number(parts[4]) : 0;
  const ss = parts.length >= 6 ? Number(parts[5]) : 0;
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(date.getTime()) ? null : date;
};

const computeCsdStatus = (certificates = []) => {
  const now = new Date();
  const list = Array.isArray(certificates) ? certificates : [];
  if (list.length === 0) {
    return { status: 'missing', expiresAt: null, daysLeft: null };
  }

  const certsWithExp = list
    .map((c) => ({
      cert: c,
      exp: parseMysqlDateTime(c?.expiration_date),
    }))
    .filter((x) => x.exp);

  if (certsWithExp.length === 0) {
    return { status: 'unknown', expiresAt: null, daysLeft: null };
  }

  const latest = certsWithExp.reduce((acc, cur) => (cur.exp > acc.exp ? cur : acc), certsWithExp[0]);
  const hasValid = certsWithExp.some((x) => x.exp.getTime() >= now.getTime());
  const daysLeft = Math.ceil((latest.exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

  if (!hasValid) return { status: 'expired', expiresAt: latest.exp, daysLeft };
  if (daysLeft <= 30) return { status: 'expiring_soon', expiresAt: latest.exp, daysLeft };
  return { status: 'valid', expiresAt: latest.exp, daysLeft };
};

const UserDashboard = () => {
  const { user } = useAuth();
  const { token } = theme.useToken();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const balanceData = await userService.getBalance();
        setBalance(balanceData);
      } catch (error) {
        console.error('Error fetching balance:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCompanies = async () => {
      try {
        const response = await companiesService.getCompanies();
        const companiesList = response.data?.companies || [];
        
        // Fetch certificate status for each company
        const companiesWithCerts = await Promise.all(
          companiesList.map(async (company) => {
            try {
              const response = await certificatesService.getCertificates(company.id);
              const certs = response.data || [];
              const csd = computeCsdStatus(certs);
              return { 
                ...company, 
                csdStatus: csd.status,
                csdExpiresAt: csd.expiresAt ? csd.expiresAt.toISOString() : null,
                csdDaysLeft: csd.daysLeft
              };
            } catch (e) {
              return { ...company, csdStatus: 'unknown', csdExpiresAt: null, csdDaysLeft: null };
            }
          })
        );
        
        setCompanies(companiesWithCerts);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchBalance();
    fetchCompanies();
  }, []);

  // Derivados de empresas (hooks antes de cualquier return temprano).
  // Resumen de salud fiscal: se calcula sobre TODAS las empresas, no la vista filtrada.
  const fiscalSummary = useMemo(() => ({
    total: companies.length,
    activas: companies.filter((c) => c.is_active).length,
    porVencer: companies.filter((c) => c.csdStatus === 'expiring_soon').length,
    vencidos: companies.filter((c) => c.csdStatus === 'expired').length,
    pendientes: companies.filter((c) => c.csdStatus === 'missing').length,
  }), [companies]);

  const filteredCompanies = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        String(c.tax_name || '').toLowerCase().includes(q) ||
        String(c.tax_id || '').toLowerCase().includes(q)
    );
  }, [companies, companySearch]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Usar datos del balance actualizado del backend
  const planCredits = balance?.plan_credits || 0;
  const planCreditsUsed = balance?.plan_credits_used || 0;
  const planCreditsAvailable = balance?.plan_credits_available || 0;
  const bulkCredits = balance?.bulk_credits || 0;
  const totalCreditsAvailable = balance?.total_credits_available || 0;
  const creditUsagePercentage = balance?.credits_usage_percentage || 0;
  const needsRenewal = balance?.needs_renewal || false;
  const planRenewalDate = balance?.plan_renewal_date;

  const csdCell = (company) => {
    const m = CSD_META[company.csdStatus] || CSD_META.unknown;
    const fecha = company.csdExpiresAt ? new Date(company.csdExpiresAt).toLocaleDateString('es-MX') : '';
    let suffix = '';
    if (company.csdStatus === 'valid' || company.csdStatus === 'expiring_soon') suffix = fecha ? ` (hasta ${fecha})` : '';
    else if (company.csdStatus === 'expired') suffix = fecha ? ` (${fecha})` : '';
    return <Text type={m.type}>{m.label}{suffix}</Text>;
  };

  return (
    <div>
      <Space align="center" style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Dashboard
        </Title>
      </Space>

      {/* Alerta si necesita renovación */}
      {needsRenewal && (
        <Alert
          message="Plan Vencido"
          description="Tu plan ha vencido. Renueva tu suscripción para seguir disfrutando de los créditos mensuales."
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Alerta si créditos bajos */}
      {totalCreditsAvailable <= 5 && totalCreditsAvailable > 0 && (
        <Alert
          message="Créditos Bajos"
          description={`Te quedan solo ${totalCreditsAvailable} créditos. Considera adquirir más créditos para continuar.`}
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Alerta si no hay créditos */}
      {totalCreditsAvailable === 0 && (
        <Alert
          message="Sin Créditos"
          description="No tienes créditos disponibles. Adquiere más créditos para poder timbrar facturas."
          type="error"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Créditos consolidados en un solo bloque, sin repetir números */}
        <Col xs={24} lg={16}>
          <Card title={<Space><CreditCardOutlined />Créditos</Space>} style={{ height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text type="secondary">Uso del plan</Text>
              <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{planCreditsUsed} / {planCredits}</Text>
            </div>
            <Progress
              percent={creditUsagePercentage}
              status={creditUsagePercentage >= 100 ? 'exception' : 'active'}
              strokeColor={creditUsagePercentage >= 80 ? token.colorError : token.colorInfo}
            />
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={8}>
                <Statistic title="Plan disponible" value={planCreditsAvailable} valueStyle={{ fontSize: 20, color: planCreditsAvailable > 0 ? token.colorSuccess : token.colorError }} />
              </Col>
              <Col xs={8}>
                <Statistic title="Adicionales" value={bulkCredits} valueStyle={{ fontSize: 20, color: palette.accentPurple }} />
              </Col>
              <Col xs={8}>
                <Statistic title="Total disponible" value={totalCreditsAvailable} valueStyle={{ fontSize: 20, fontWeight: 700, color: totalCreditsAvailable > 10 ? token.colorSuccess : totalCreditsAvailable > 0 ? token.colorWarning : token.colorError }} />
              </Col>
            </Row>
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 12 }}>
              {planRenewalDate && (
                <><CalendarOutlined /> <strong>Renovación:</strong> {new Date(planRenewalDate).toLocaleDateString('es-MX')} · </>
              )}
              Los créditos del plan se renuevan cada mes; los adicionales vencen en 7 días.
            </div>
          </Card>
        </Col>

        {/* Actividad, separada de los créditos */}
        <Col xs={24} lg={8}>
          <Card title="Actividad" style={{ height: '100%' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Statistic title="Facturas generadas" value={balance?.invoice_count || 0} prefix={<FileTextOutlined />} valueStyle={{ color: token.colorInfo }} />
              <Statistic title="Nóminas generadas" value={balance?.nomina_count || 0} prefix={<TeamOutlined />} valueStyle={{ color: palette.accentPurple }} />
            </Space>
          </Card>
        </Col>
      </Row>

      {/* SALUD FISCAL: enseguida de créditos, con resumen y empresas que escalan */}
      <Card
        title={<Space><SafetyCertificateOutlined /><span>Salud fiscal</span></Space>}
        extra={
          companies.length > 0 ? (
            <Space size={4} wrap>
              <Tag icon={<CheckCircleOutlined />} color="success">{fiscalSummary.activas} activas</Tag>
              {fiscalSummary.porVencer > 0 && <Tag color="warning">{fiscalSummary.porVencer} por vencer</Tag>}
              {fiscalSummary.vencidos > 0 && <Tag color="error">{fiscalSummary.vencidos} vencidos</Tag>}
              {fiscalSummary.pendientes > 0 && <Tag>{fiscalSummary.pendientes} sin CSD</Tag>}
            </Space>
          ) : null
        }
      >
        {companies.length === 0 ? (
          <Empty description="No tienes empresas registradas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar empresa por nombre o RFC"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              style={{ maxWidth: 360, marginBottom: 16 }}
            />

            {filteredCompanies.length === 0 ? (
              <Empty description="Sin resultados para la búsqueda" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              /* Carril horizontal: el alto no crece con el número de empresas */
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  overflowX: 'auto',
                  paddingBottom: 8,
                  scrollSnapType: 'x proximity',
                }}
              >
                {filteredCompanies.map((company) => (
                  <Card
                    key={company.id}
                    type="inner"
                    size="small"
                    style={{ flex: '0 0 288px', scrollSnapAlign: 'start' }}
                    title={
                      <Text ellipsis style={{ maxWidth: 190 }} title={company.tax_name}>
                        {company.tax_name}
                      </Text>
                    }
                    extra={
                      company.is_active ? (
                        <Text type="success" style={{ fontSize: 12 }}><CheckCircleOutlined /> Activa</Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}><CloseCircleOutlined /> Inactiva</Text>
                      )
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">RFC:</Text>
                        <Text copyable>{company.tax_id}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">Régimen:</Text>
                        <Text ellipsis style={{ maxWidth: 160 }} title={company.fiscal_regimen}>
                          {company.fiscal_regimen || 'No definido'}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, borderTop: `1px solid ${token.colorSplit}`, paddingTop: 8 }}>
                        <Text type="secondary">CSD:</Text>
                        {csdCell(company)}
                      </div>
                    </Space>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default UserDashboard;
